import React, { useCallback } from 'react'
import { DialogTitle } from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { ChatInput } from './../ChatInput'
import { ChatHeader } from './../ChatHeader'
import { ChatMessages } from './../ChatMessages'
import { InputSuggestions } from '../InputSuggestions'
import { DisclaimerMessage } from './../components/DisclaimerMessage'
import { useChatModeContext } from '../ChatContext'
import { useLocaleText } from './../locale'
import { ConciergeProductModal } from '@moonpig/web-shared-products'
import { ErrorScreen } from '../components/ErrorScreen/ErrorScreen'
import { ChatMode } from '../hooks/types'
import { LoadingIndicator } from '@moonpig/web-shared-components'
import { SideModal } from '../SideModal'
import {
  ConciergeChatBody,
  ConciergeChatLoadingWrapper,
} from './ConciergeChat.styled'

export const ConciergeChat = () => {
  const t = useLocaleText()
  const {
    isChatModalOpen,
    hideChatModal,
    switchToChatMode,
    selectedProductId,
    chatMode,
    shouldShowDisclaimer,
    isOptionsMenuOpen,
  } = useChatModeContext()

  const onAddToBasket = useCallback(() => {
    switchToChatMode()
  }, [switchToChatMode])

  const chatModeConfig = {
    [ChatMode.LOADING_CONVERSATION]: {
      content: <LoadingIndicator label={t('concierge_chat.loading_label')} />,
      header: <ChatHeader />,
      description: t('concierge_chat.modal_description'),
      isRadialBackground: true,
      onBack: undefined,
    },
    [ChatMode.CHAT]: {
      content: (
        <ConciergeChatBody data-testid="concierge-chat-body">
          <VisuallyHidden>
            {/* Adding a dialog title is required by radix but can be visually hidden as we render the title elsewhere */}
            <DialogTitle>{t('concierge_chat.modal_title')}</DialogTitle>
          </VisuallyHidden>
          <ChatMessages />
          <InputSuggestions />
          <ChatInput />
          {shouldShowDisclaimer && <DisclaimerMessage />}
        </ConciergeChatBody>
      ),
      header: <ChatHeader />,
      description: t('concierge_chat.modal_description'),
      isRadialBackground: true,
      onBack: undefined,
    },
    [ChatMode.PRODUCT_DETAILS]: {
      content: (
        <ConciergeProductModal
          selectedProductId={selectedProductId}
          onAddToBasket={onAddToBasket}
        />
      ),
      header: undefined,
      description: t('concierge_chat.product_modal_description'),
      isRadialBackground: false,
      onBack: switchToChatMode,
    },
    [ChatMode.ERROR_SCREEN]: {
      content: <ErrorScreen />,
      header: <ChatHeader />,
      description: t('concierge_chat.error_modal_description'),
      isRadialBackground: true,
      onBack: undefined,
    },
  }

  const currentChatModeConfig =
    chatModeConfig[chatMode] || chatModeConfig[ChatMode.CHAT]

  return (
    <SideModal
      preventESCDismiss={isOptionsMenuOpen}
      customHeader={currentChatModeConfig.header}
      backButton
      onDismiss={hideChatModal}
      isOpen={isChatModalOpen}
      backgroundColor={
        currentChatModeConfig.isRadialBackground
          ? undefined
          : 'colorBackground01'
      }
      isRadialBackground={currentChatModeConfig.isRadialBackground}
      width={{ xs: '100%', md: '35%' }}
      minWidth={{ md: '390px' }}
      maxWidth={null}
      labels={{
        backButton: '',
        closeButton: '',
      }}
      description={currentChatModeConfig.description}
      onBack={currentChatModeConfig.onBack}
      isChatVariant
    >
      <ConciergeChatLoadingWrapper
        aria-busy={
          chatMode === ChatMode.LOADING_CONVERSATION ? 'true' : 'false'
        }
        aria-live={
          chatMode === ChatMode.LOADING_CONVERSATION ? 'polite' : 'off'
        }
        role="status"
      >
        {currentChatModeConfig.content}
      </ConciergeChatLoadingWrapper>
    </SideModal>
  )
}
